'use client';

import React, { useRef, useState, useEffect } from 'react';
import { Download, Loader2 } from 'lucide-react';
import ShareCard, { ShareCardArticle } from '@/components/public/ShareCard';
import { captureShareCardElement } from '@/lib/shareCardCapture';

interface Props {
    article: ShareCardArticle;
}

/** Same-origin opaque paths skip /api/proxy-image (avoids broken localhost self-fetch). */
function resolveShareImageSrc(imageUrl: string): string {
    if (imageUrl.startsWith('/')) {
        return `${window.location.origin}${imageUrl}`;
    }
    return `${window.location.origin}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
}

export default function ShareImageGenerator({ article }: Props) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    const articleUrl = `https://muktokontho.org/news/${article.id}/${article.slug || 'article'}`;
    const imageProxyUrl =
        typeof window !== 'undefined' && article.image_url
            ? resolveShareImageSrc(article.image_url)
            : undefined;

    const handleDownload = async () => {
        if (!cardRef.current) return;

        try {
            setIsGenerating(true);
            const dataUrl = await captureShareCardElement(cardRef.current);

            const link = document.createElement('a');
            link.download = `muktokontho-${article.id}-share.png`;
            link.href = dataUrl;
            link.click();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Unknown';
            console.error('Failed to generate share image:', err);
            alert(`দুঃখিত, ছবি তৈরি করতে সমস্যা হয়েছে। \nError: ${message}`);
        } finally {
            setIsGenerating(false);
        }
    };

    if (!mounted) return null;

    return (
        <>
            <button
                onClick={handleDownload}
                disabled={isGenerating || (!isImageLoaded && !!article.image_url)}
                className="absolute top-4 right-4 z-30 bg-white/90 backdrop-blur-md text-slate-800 hover:text-red-600 px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all font-bold text-sm border border-slate-200/50 disabled:opacity-50 disabled:cursor-not-allowed group font-bengali"
            >
                {isGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin text-red-600" />
                ) : (
                    <Download className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
                )}
                {isGenerating ? 'প্রসেসিং...' : 'শেয়ার ছবি ডাউনলোড'}
            </button>

            <div
                className="fixed -left-[9999px] -top-[9999px] pointer-events-none overflow-hidden"
                style={{ opacity: 0.1, width: '1200px', minWidth: '1200px' }}
            >
                <ShareCard
                    article={article}
                    articleUrl={articleUrl}
                    imageProxyUrl={imageProxyUrl}
                    cardRef={cardRef}
                    onImageLoad={() => setIsImageLoaded(true)}
                    onImageError={() => {
                        /* Keep button disabled — do not export blank-photo PNGs */
                        console.error('Share card image failed to load:', article.image_url);
                    }}
                />
            </div>
        </>
    );
}
